import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { FileAttachment } from './FileUpload';

interface ImageGalleryProps {
  images: FileAttachment[];
  initialIndex?: number;
  onClose: () => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentImage = images[currentIndex];

  const nextImage = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
    resetZoom();
  };

  const prevImage = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
    resetZoom();
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleImageKeyDown = (e: React.KeyboardEvent) => {
    if (zoom === 1 && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleZoomIn();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = () => {
    if (currentImage.preview) {
      const link = document.createElement('a');
      link.href = currentImage.preview;
      link.download = currentImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        prevImage();
        break;
      case 'ArrowRight':
        nextImage();
        break;
      case 'Escape':
        onClose();
        break;
      case '+':
      case '=':
        handleZoomIn();
        break;
      case '-':
        handleZoomOut();
        break;
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
      {/* Header com controles */}
      <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-50 p-4 flex items-center justify-between z-10">
        <div className="flex items-center space-x-4 text-white">
          <span className="text-sm">
            {currentIndex + 1} de {images.length}
          </span>
          <span className="text-sm font-medium">{currentImage.name}</span>
        </div>

        <div className="flex items-center space-x-2">
          {/* Controles de zoom */}
          <button
            onClick={handleZoomOut}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded"
            title="Diminuir zoom"
          >
            <ZoomOut className="h-5 w-5" />
          </button>

          <span className="text-white text-sm min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>

          <button
            onClick={handleZoomIn}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded"
            title="Aumentar zoom"
          >
            <ZoomIn className="h-5 w-5" />
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded"
            title="Baixar imagem"
          >
            <Download className="h-5 w-5" />
          </button>

          {/* Fechar */}
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded"
            title="Fechar (ESC)"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Navegação lateral esquerda */}
      {images.length > 1 && (
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-10"
          title="Imagem anterior (←)"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}

      {/* Navegação lateral direita */}
      {images.length > 1 && (
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-10"
          title="Próxima imagem (→)"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}

      {/* Imagem principal */}
      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={zoom === 1 ? handleZoomIn : undefined}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && zoom === 1) {
            e.preventDefault();
            handleZoomIn();
          }
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        role={zoom === 1 ? 'button' : undefined}
        tabIndex={zoom === 1 ? 0 : -1}
        aria-label={zoom === 1 ? 'Clique para ampliar a imagem' : undefined}
      >
        {currentImage.preview && (
          <img
            src={currentImage.preview}
            alt={currentImage.name}
            className={`max-w-none transition-transform duration-200 ${
              zoom > 1 ? 'cursor-move' : 'cursor-zoom-in'
            }`}
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              maxHeight: zoom === 1 ? '90vh' : 'none',
              maxWidth: zoom === 1 ? '90vw' : 'none',
            }}
            draggable={false}
          />
        )}
      </div>

      {/* Thumbnails na parte inferior */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-black bg-opacity-50 p-3 rounded-lg">
          {images.map((image, index) => (
            <button
              key={image.id}
              onClick={() => {
                setCurrentIndex(index);
                resetZoom();
              }}
              className={`relative w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-white' : 'border-transparent hover:border-gray-400'
              }`}
            >
              {image.preview && (
                <img src={image.preview} alt={image.name} className="w-full h-full object-cover" />
              )}
              {index === currentIndex && (
                <div className="absolute inset-0 bg-white bg-opacity-20" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Instruções de uso */}
      <div className="absolute bottom-4 right-4 text-white text-xs bg-black bg-opacity-50 p-2 rounded">
        <div>← → Navegar</div>
        <div>+ - Zoom</div>
        <div>ESC Fechar</div>
      </div>
    </div>
  );
};

export default ImageGallery;
